/**
 * HermesDashboardTile — embeds the per-tenant hermes-dashboard web GUI (the
 * "GUI version of the hermes agent") on the Terminals page via the same-origin
 * reverse proxy in vibe-backend (routes/hermes_dashboard.py).
 *
 * Flow:
 *   1. On mount, POST /api/hermes-dashboard/authorize (Bearer via services/api,
 *      same-origin ⇒ the response Set-Cookie is stored by the browser). This
 *      mints the short-lived signed cookie the iframe's browser-initiated
 *      sub-requests (which can't carry a Bearer header) present to the proxy.
 *   2. Render <iframe src="/api/hermes-dashboard/app/">. Because the proxy is
 *      same-origin, the iframe + all its assets/API/WS ride the cookie, and the
 *      parent could (Part B) inject theme CSS into it.
 *
 * The cookie has a TTL (1h); we re-authorize on a timer at ~half-life so a
 * long-open tab keeps working, and re-authorize on iframe load error.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTenantStore } from '@/stores/tenantStore';
import {
  GHL_THEMES,
  loadHermesThemeId,
  saveHermesThemeId,
  type GhlThemeId,
} from '@/shared/theme/agentThemes';
import {
  HERMES_APP_SRC as HERMES_IFRAME_SRC,
  HERMES_REAUTHORIZE_INTERVAL_MS as REAUTHORIZE_INTERVAL_MS,
  authorizeHermesDashboard,
  injectHermesTheme,
} from '@/services/hermesEmbed';

type Status = 'authorizing' | 'ready' | 'error';

export function HermesDashboardTile() {
  const [status, setStatus] = useState<Status>('authorizing');
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  // Per-tenant hermes theme (operator 2026-07-04: apply the GHL themes to
  // hermes too). The hermes GUI is a SINGLE surface — one picker, persisted
  // per-tenant (its own key, independent of the per-tab Pi chat theme).
  const tenantId = useTenantStore((s) => s.tenantId);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [themeId, setThemeId] = useState<GhlThemeId>(() => loadHermesThemeId(tenantId));

  // When the tenant resolves after mount, adopt its persisted pick.
  useEffect(() => {
    setThemeId(loadHermesThemeId(tenantId));
  }, [tenantId]);

  // Inject (or update) the GHL-theme override stylesheet inside the same-origin
  // iframe (shared with HermesChatFrame via services/hermesEmbed).
  const applyHermesTheme = useCallback((id: GhlThemeId) => {
    injectHermesTheme(iframeRef.current?.contentDocument, id);
  }, []);

  // Re-inject whenever the picked theme changes (the iframe onLoad handles the
  // first paint + any reload).
  useEffect(() => {
    if (status === 'ready') applyHermesTheme(themeId);
  }, [themeId, status, applyHermesTheme]);

  const onThemeChange = useCallback(
    (id: GhlThemeId) => {
      setThemeId(id);
      saveHermesThemeId(tenantId, id);
    },
    [tenantId],
  );

  const authorize = useCallback(async (): Promise<boolean> => {
    try {
      await authorizeHermesDashboard();
      return true;
    } catch (e) {
      if (mounted.current) {
        setError(e instanceof Error ? e.message : String(e));
        setStatus('error');
      }
      return false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void (async () => {
      const ok = await authorize();
      if (ok && mounted.current) setStatus('ready');
    })();
    const timer = window.setInterval(() => void authorize(), REAUTHORIZE_INTERVAL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [authorize]);

  if (status === 'authorizing') {
    return (
      <div
        className="h-full w-full flex items-center justify-center text-vibe-text-secondary text-sm"
        data-testid="hermes-dashboard-loading"
      >
        Connecting to Hermes…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center gap-2 text-sm"
        data-testid="hermes-dashboard-error"
      >
        <p className="text-vibe-error">Couldn’t reach the Hermes dashboard.</p>
        {error && <p className="text-vibe-text-secondary text-xs max-w-md truncate" title={error}>{error}</p>}
        <button
          type="button"
          onClick={() => {
            setStatus('authorizing');
            setError(null);
            void (async () => {
              const ok = await authorize();
              if (ok && mounted.current) setStatus('ready');
            })();
          }}
          className="px-3 py-1 rounded border border-vibe-border bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary text-vibe-text-secondary hover:text-vibe-text text-xs"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Thin header: theme picker for the hermes GUI (single surface). */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-b border-vibe-border bg-vibe-bg-secondary px-3 py-1.5">
        <span className="text-[11px] text-vibe-text-secondary">Theme</span>
        <select
          value={themeId}
          onChange={(e) => onThemeChange(e.target.value as GhlThemeId)}
          data-testid="hermes-dashboard-theme-picker"
          aria-label="Hermes GUI theme"
          title="Theme for the Hermes GUI"
          className="rounded border border-vibe-border bg-vibe-bg px-1.5 py-0.5 text-[11px] text-vibe-text-secondary hover:text-vibe-text"
        >
          {Object.values(GHL_THEMES).map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>
      <iframe
        ref={iframeRef}
        src={HERMES_IFRAME_SRC}
        title="Hermes Dashboard"
        data-testid="hermes-dashboard-iframe"
        className="min-h-0 w-full flex-1 border-0 bg-vibe-bg"
        // The dashboard needs script + same-origin (it IS same-origin via the
        // proxy) + forms; allow-popups for any external links it opens.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        // Inject the GHL theme as soon as the iframe document is available (also
        // fires after any reload triggered by onError → re-authorize).
        onLoad={() => applyHermesTheme(themeId)}
        onError={() => void authorize()}
      />
    </div>
  );
}
